import { Anchor, Radio, Stack } from '@vtex/admin-ui'
import { RadioGroup } from '@vtex/admin-ui-form'
import { useDrawerSubForm } from 'hooks'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Affiliates, OPERATORS } from 'typings'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  SaveButton,
  UseDrawerStateReturn,
} from '../../../..'
import { AffiliatesSelector } from '../../Selectors/AffiliatesSelector'
import { messages } from './messages'

interface Props {
  state: UseDrawerStateReturn
  initialConfig: Affiliates | undefined
  onSave: (config: Affiliates | undefined) => void
}

const EMPTY_CONFIG: NonNullable<Affiliates> = Object.freeze({
  affiliates: [],
  operator: OPERATORS.EQUALS,
})

export function Editor(props: Props) {
  const { state, onSave, initialConfig } = props
  const { hide } = state

  const isCreating = initialConfig === undefined
  const { $t } = useIntl()

  const config = useMemo(
    () => initialConfig ?? { ...EMPTY_CONFIG },
    [initialConfig]
  )

  const { drawerForm } = useDrawerSubForm<NonNullable<Affiliates>>({
    config,
    isOpen: state.open,
  })

  const handleSave = async () => {
    const isValid = await drawerForm.trigger()

    if (isValid) {
      const values = drawerForm.getValues()
      const hasAffiliates = values.affiliates.length > 0
      onSave(hasAffiliates ? values : undefined)
      hide()
    }
  }

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>{$t(messages.title)}</DrawerTitle>
        <DrawerSubtitle>
          {$t(messages.editor.description, {
            a: (content: any) => (
              <Anchor
                target="_blank"
                href={$t(messages.editor.moreInformation)}
              >
                {content}
              </Anchor>
            ),
          })}
        </DrawerSubtitle>
        <DismissButton onHide={state.hide} />
      </DrawerHeader>
      <DrawerContent>
        <Stack space="$space-3" fluid>
          <RadioGroup
            name="operator"
            state={drawerForm}
            label={null}
            direction="row"
          >
            {[OPERATORS.EQUALS, OPERATORS.DIFFERENT].map((value) => (
              <Radio
                key={value}
                label={$t(messages.editor[value])}
                value={value}
              />
            ))}
          </RadioGroup>
          <AffiliatesSelector name="affiliates" state={drawerForm} />
        </Stack>
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={hide} />
        <SaveButton isCreating={isCreating} onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
